import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { formatBid, bumpRatePercentToDecimal } from './bump_helpers';
import {
  MAX_BUMP_AMOUNT_PERCENT,
  MIN_BUMP_AMOUNT_PERCENT,
  BUMP_INCREMENT_AMOUNT_PERCENT,
} from '../shared/constants';
import { RCAlertBox, RCIcon, RCTag, useMediaQuery } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { LightningDuotoneIcon } from '@reverbdotcom/cadence/icons/react';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';

function RecommendedRate({ percent }) {
  return (
    <div className="d-flex fx-align-center">
      <RCTextUtility size="400" weight="medium">
        <b><Translate text="discovery.bumpModal.recommendedRateAmount" args={{ percent }}></Translate></b>
      </RCTextUtility>
    </div>
  );
}

function CustomRate() {
  return (
    <RCTextUtility size="400" weight="medium" >
      {I18n.t('discovery.bumpModal.customRate')}
    </RCTextUtility>
  );
}

export function RecommendedRadioV3({
  recommendationAmount,
  selectedBid,
  handleBidChange,
  showCustomWarning,
  setShowCustomWarning,
  minimumBumpBid,
}: {
  recommendationAmount: number;
  selectedBid: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (boolean) => void;
  minimumBumpBid?: number;
}) {
  const SELL_THROUGH_PERCENT = '25';
  const [usingCustom, setUsingCustom] = React.useState(
    (selectedBid !== 0 && ((bumpRatePercentToDecimal(recommendationAmount)) !== selectedBid)),
  );

  const [usingRecommended, setUsingRecommended] = React.useState(
    !usingCustom,
  );

  const [customBid, setCustomBid] = React.useState(
    (selectedBid !== 0) ? (formatBid(selectedBid)) : recommendationAmount,
  );

  React.useEffect(() => {
    if (usingRecommended) {
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    }
  }, [usingRecommended]);

  const handleRecommendedClick = (): void => {
    setUsingRecommended(true);
    setUsingCustom(false);
    setShowCustomWarning(false);

    handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
  };

  const handleManualClick = (): void => {
    setUsingRecommended(false);
    setUsingCustom(true);

    setCustomWarning(customBid);
    handleBidChange(bumpRatePercentToDecimal(customBid));
  };

  const handleCustomSelectChange = (event: any) => {
    const bid = event?.target?.value;
    setCustomWarning(bid);
    setCustomBid(bid);
    handleBidChange(bumpRatePercentToDecimal(bid));
  };

  const setCustomWarning = (bid: number) => {
    if ((bid >= (minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT)) &&
      (bid <= MAX_BUMP_AMOUNT_PERCENT) &&
      (bid % BUMP_INCREMENT_AMOUNT_PERCENT === 0)) {
      setShowCustomWarning(false);
    } else {
      setShowCustomWarning(true);
    }
  };

  const isMobile = useMediaQuery('mobile');

  return (
    <>
      <div className={classNames(
        'bd-2 bd-radius-md padding-4',
        { 'bd-color-selected': usingRecommended },
        { 'bd-color-selectable': !(usingRecommended) },
      )}>
        <div className="bump-modal-iteration__recommended-row" >
          <FormGroupWithRadio
            label={<RecommendedRate percent={recommendationAmount} />}
            inputName="bump-modal-radios"
            id="bump-modal-radio-recommended"
            updateField={handleRecommendedClick}
            value={`${recommendationAmount / 100}`}
            checked={usingRecommended}
          />
          <div className="d-flex fx-align-center ml-2">
            <RCTag variant="highlight">
              {I18n.t('discovery.bumpModal.recommended')}
            </RCTag>
          </div>
        </div>

        <div
          className="d-flex fx-justify-start fx-align-center ml-7 mt-2 bd-radius-sm padding-2"
          style={{ backgroundColor: 'var(--rc-color-background-neutral-ultralight)', width: 'auto' }}
        >
          <div className="d-flex text-color-accent fx-justify-start fx-align-center pr-2">
            <RCIcon svgComponent={LightningDuotoneIcon} size={isMobile ? 'rc-space-6' : 'rc-space-8'} />
          </div>
          <div className="d-flex fx-dir-col fx-justify-center">
            <RCTextUtility size="350" weight="bold">
              <Translate
                text="discovery.bumpModal.sellThroughCalloutIteration"
                args={{ percent: SELL_THROUGH_PERCENT }}
                className="text-color-primary mr-space"
              />
            </RCTextUtility>
            <RCTextUtility size="350" color="rc-color-text-secondary">
              <Translate text="discovery.bumpModal.sellThroughCalloutSubtitle" />
            </RCTextUtility>
          </div>
        </div>
      </div>

      <div className={classNames(
        'bd-2 bd-radius-md padding-4 mt-2',
        { 'bd-color-selected': usingCustom },
        { 'bd-color-selectable': !(usingCustom) },
      )}>
        <FormGroupWithRadio
          label={<CustomRate />}
          inputName="bump-modal-radios"
          id="bump-modal-radio-custom"
          updateField={handleManualClick}
          value={`${recommendationAmount / 100}`}
          checked={usingCustom}
        />
        {
          usingCustom &&
          <div className="width-20 mobile-width-100">
            <div className="input-group">
              <input
                type="number"
                className="input-group__input"
                disabled={!(usingCustom)}
                value={customBid}
                onChange={handleCustomSelectChange}
              />
              <div className="input-group__text">%</div>
            </div>
          </div>
        }
        {
          showCustomWarning && <div className="mt-2">
            <RCAlertBox type="error" small>
              {I18n.t('discovery.bumpModal.customBumpWarning', {
                minBump: (minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT),
                maxBump: MAX_BUMP_AMOUNT_PERCENT,
                bumpIncrement: BUMP_INCREMENT_AMOUNT_PERCENT,
              })}
            </RCAlertBox>
          </div>
        }
      </div>
    </>
  );
}
